package games.polyclub.website.frontend.store.pages.logged.inventory

import games.polyclub.frontend.lib.messagerenderer.Viewer
import games.polyclub.frontend.lib.messagerenderer.m
import games.polyclub.frontend.lib.react.bootstrap.components.Card
import games.polyclub.frontend.lib.react.bootstrap.custom.oldcomponents.CountdownProgressBar
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.new
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classes
import games.polyclub.message.BuildString
import games.polyclub.message.div
import games.polyclub.message.unaryMinus
import games.polyclub.service.payment.api.id.ProductId
import games.polyclub.service.player.id.api.SystemPlayerId
import games.polyclub.service.profile.api.PlayerProfile
import games.polyclub.service.store.api.ConsumableItem
import games.polyclub.service.store.api.ConsumableItem.TimerEvent
import games.polyclub.service.store.api.Product
import games.polyclub.service.store.api.id.ItemUpdateId
import games.polyclub.tool.functional.Either
import games.polyclub.tool.functional.Option
import games.polyclub.tool.strings.formatted
import games.polyclub.tool.strings.toClockFormat
import games.polyclub.tool.strings.toExtensiveFormat
import games.polyclub.website.frontend.store.components.PlayerName
import games.polyclub.website.frontend.store.components.ProductName
import games.polyclub.website.frontend.store.sitemap.Icon
import games.polyclub.website.frontend.store.sitemap.LoggedSitemap
import js.core.jso
import kotlinx.datetime.Instant
import react.Fragment
import react.Props
import react.ReactElement
import react.router.dom.Link
import web.cssom.NamedColor
import web.cssom.px
import web.cssom.rem
import kotlin.time.Duration.Companion.seconds

external interface InventoryFocusComponentProps : Props {
    var profile: PlayerProfile
    var now: Instant
    var item: ConsumableItem
    var goBack: () -> Unit
}

val InventoryFocusPage = SFC<InventoryFocusComponentProps> { props ->
    val item = props.item
    val profile = props.profile
    val now = props.now
    Card {
        title = "Inventário → ${item.name - BuildString(Viewer)}"
        footer = button.new {
            classes = "btn btn-primary"
            onClick = { props.goBack() }
            +"Voltar"
        }
        div("row") {
            div("col-3") {
                when (item.productId) {
                    ProductId.ALL_OF_FABRIC_ACCESS_PASS -> Icon.src("/img/aof7/icon32x32.png", 256.px, 256.px)
                    ProductId.SKYFACTORY_ACCESS_PASS -> Icon.src("/img/skyfactory/icon.png", 256.px, 256.px)
                    else -> Icon.fas("fa-cog")
                }.apply { renderIcon() }
            }
            div("col-9") {
                table("table table-striped") {
                    tr {
                        th("border-0") {
                            style = jso {
                                width = 9.rem
                            }
                            +"Tipo:"
                        }
                        td("border-0") {
                            when (item.productType) {
                                Product.Type.COIN -> {
                                    +"Moeda"
                                    p("mb-0") {
                                        small {
                                            +"Moedas são usadas como item de troca no servidor"
                                        }
                                    }
                                }

                                Product.Type.TEMPORAL -> {
                                    +"Temporal"
                                    p("mb-0") {
                                        small {
                                            +"Ao ser consumido este item ficará ativo por um tempo determinado"
                                        }
                                    }
                                }

                                Product.Type.CONSUMABLE -> {
                                    +"Consumível"
                                    p("mb-0") {
                                        small {
                                            +"Utilizado completamente ao ser consumido"
                                        }
                                    }
                                }
                            }
                        }
                    }
                    tr {
                        th {
                            +"Descrição:"
                        }
                        td {
                            m(item.description)
                        }
                    }
                    tr {
                        th {
                            +"Disponível:"
                        }
                        td {
                            +item.amount.available.toString()
                            p("mb-0") {
                                small {
                                    +"A quantidade que você tem disponível para consumir"
                                }
                            }
                        }
                    }
                    if (item.productType == Product.Type.TEMPORAL) {
                        val activePeriod = item.timer.activePeriod()
                        tr {
                            th {
                                +"Tempo Restante:"
                            }
                            td {
                                if (activePeriod == null) {
                                    em {
                                        +"O item não foi ativado"
                                    }
                                } else {
                                    val predictedEnd = activePeriod.predictedEnd()
                                    if (predictedEnd == null) {
                                        em {
                                            +"Indeterminado"
                                        }
                                    } else {
                                        CountdownProgressBar::class {
                                            this.classes = "my-1"
                                            this.min = activePeriod.started!!.at
                                            this.max = predictedEnd
                                            showClock = true
                                            customFormatter = { _, _, current ->
                                                current.coerceAtLeast(0.seconds).toExtensiveFormat(minutes = true)
                                            }
                                        }
                                    }
                                }
                                p("mb-0") {
                                    small {
                                        +"O tempo que falta para o item ativo expirar"
                                    }
                                }
                            }
                        }
                        tr {
                            th {
                                +"Total Restante:"
                            }
                            td {
                                val remaining = (activePeriod?.timeRemaining() ?: 0.seconds) +
                                        item.timer.nextPendingPeriods().map { it.timeRemaining() }
                                            .fold(0.seconds) { acc, next -> acc + next }
                                if (remaining > 0.seconds) {
                                    +remaining.toExtensiveFormat()
                                } else {
                                    em {
                                        +"Não há tempo restante."
                                    }
                                }
                                p("mb-0") {
                                    small {
                                        +"O tempo que falta para todos os itens expirarem quando ativos"
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    val updateReason = mutableMapOf<ItemUpdateId, ReactElement<*>>()
    Card {
        title = "Histórico de Uso"
        table("table table-striped") {
            thead {
                tr {
                    th {
                        style = jso {
                            width = 10.rem
                        }
                        +"Data / Hora"
                    }
                    th {
                        style = jso {
                            width = 3.rem
                        }
                        +"Mudança"
                    }
                    th {
                        style = jso {
                            width = 3.rem
                        }
                        +"Saldo"
                    }
                    th {
                        style = jso {
                            width = 19.rem
                        }
                        +"Pedido"
                    }
                    th {
                        +"Motivo"
                    }
                }
            }
            tbody {
                var count = 0
                item.amount.events.forEach { event ->
                    tr {
                        td {
                            +event.time.formatted()
                        }
                        td {
                            classes = when {
                                event.change < 0 -> "text-center text-danger"
                                event.change == 0 -> "text-center text-gray"
                                else -> "text-center text-success"
                            }
                            count += event.change
                            if (event.change > 0) +"+"
                            +event.change.toString()
                        }
                        td {
                            classes = when {
                                count <= 0 -> "text-center text-danger"
                                else -> "text-center text-success"
                            }
                            +count.toString()
                        }
                        td {
                            Option.of(event.orderId)
                                .ifEmpty {
                                    small("text-gray") {
                                        +"N/A"
                                    }
                                }.ifPresent {
                                    Link {
                                        to = "${LoggedSitemap.PAY_ROOT.path}/$it"
                                        +it.toString()
                                    }
                                }
                        }
                        td {
                            val reason: ReactElement<*> = when (event) {
                                is ConsumableItem.AmountUpdate.Decrease -> {
                                    when (event.reason) {
                                        ConsumableItem.AmountUpdate.DecreaseReason.ADM_TAKE -> Fragment.new {
                                            m(-"Removido ${"pela"/event.takenBy} ${"administradora"/event.takenBy} ")
                                            strong {
                                                PlayerName {
                                                    playerId = event.takenBy
                                                }
                                            }
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.GIFT_GIVEN -> Fragment.new {
                                            val receiver = event.giftedTo
                                            if (receiver == SystemPlayerId.GIVEAWAY) {
                                                strong {
                                                    +"Doado para sorteio"
                                                }
                                            } else {
                                                +"Dado de presente para "
                                                strong {
                                                    PlayerName {
                                                        playerId = receiver
                                                    }
                                                }
                                            }
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.TRANSFERRED -> Fragment.new {
                                            val receiver = event.transferredTo
                                            +"Transferido para "
                                            strong {
                                                if (receiver == SystemPlayerId.GIVEAWAY) {
                                                    +"ser sorteado"
                                                } else {
                                                    PlayerName {
                                                        playerId = receiver
                                                    }
                                                }
                                            }
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.CONSUMED -> Fragment.new {
                                            m(-"Consumido ${"pela"/profile} ${"jogadora"/profile}")
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.CHARGEBACK -> Fragment.new {
                                            +"Compra estornada"
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.REFUNDED -> Fragment.new {
                                            +"Compra devolvida"
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.REVERTED -> Fragment.new {
                                            +"Revertido"
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.UNSPECIFIED -> Fragment.new {
                                            +"Não Especificado"
                                        }
                                        ConsumableItem.AmountUpdate.DecreaseReason.CONVERTED -> Fragment.new {
                                            +"Convertido"
                                            event.productId?.let {
                                                +" em "
                                                strong {
                                                    ProductName {
                                                        productId = it
                                                    }
                                                }
                                            }
                                            event.takenBy?.let {
                                                +" por "
                                                strong {
                                                    PlayerName {
                                                        playerId = it
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                is ConsumableItem.AmountUpdate.Increase -> {
                                    when (event.reason) {
                                        ConsumableItem.AmountUpdate.IncreaseReason.ADM_GIVE -> Fragment.new {
                                            m(-"Dado ${"pela"/event.givenByPlayer} ${"administradora"/event.givenByPlayer} ")
                                            strong {
                                                PlayerName {
                                                    playerId = event.givenByPlayer
                                                }
                                            }
                                        }
                                        ConsumableItem.AmountUpdate.IncreaseReason.PLAYER_GIFT -> Fragment.new {
                                            if (event.givenByPlayer == SystemPlayerId.GIVEAWAY) {
                                                +"Obtido por um sorteio"
                                            } else {
                                                m(-"Recebido de presente ${"da" / event.givenByPlayer} ")
                                                strong {
                                                    PlayerName {
                                                        playerId = event.givenByPlayer
                                                    }
                                                }
                                            }
                                        }
                                        ConsumableItem.AmountUpdate.IncreaseReason.PLAYER_TRANSFER -> Fragment.new {
                                            if (event.givenByPlayer == SystemPlayerId.GIVEAWAY) {
                                                +"Obtido por um sorteio (por meio de transferência)"
                                            } else {
                                                m(-"Transferido ${"da" / event.givenByPlayer} ")
                                                strong {
                                                    PlayerName {
                                                        playerId = event.givenByPlayer
                                                    }
                                                }
                                            }
                                        }
                                        ConsumableItem.AmountUpdate.IncreaseReason.REWARD -> Fragment.new {
                                            +"Obtido como uma recompensa"
                                        }
                                        ConsumableItem.AmountUpdate.IncreaseReason.PURCHASE -> Fragment.new {
                                            +"Comprado"
                                        }
                                        ConsumableItem.AmountUpdate.IncreaseReason.REVERTED -> Fragment.new {
                                            +"Revertido"
                                        }
                                        ConsumableItem.AmountUpdate.IncreaseReason.CONVERTED -> Fragment.new {
                                            +"Convertido"
                                            event.givenByProduct?.let {
                                                +" de "
                                                strong {
                                                    ProductName {
                                                        productId = it
                                                    }
                                                }
                                            }
                                            event.givenByPlayer?.let {
                                                +" por "
                                                strong {
                                                    PlayerName {
                                                        playerId = it
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            updateReason[event.id!!] = reason
                            +reason
                        }
                    }
                }
            }
        }
    }

    if (item.productType == Product.Type.TEMPORAL) {
        Card {
            title = "Cronômetro"
            table("table table-striped") {
                thead {
                    tr {
                        th {
                            style = jso {
                                width = 10.rem
                            }
                            +"Data / Hora"
                        }
                        th {
                            style = jso {
                                width = 3.rem
                            }
                            +"Ação"
                        }
                        th {
                            style = jso {
                                width = 3.rem
                            }
                            +"Mudança"
                        }
                        th {
                            style = jso {
                                width = 3.rem
                            }
                            +"Saldo"
                        }
                        th {
                            style = jso {
                                width = 19.rem
                            }
                            +"Pedido"
                        }
                        th {
                            +"Motivo"
                        }
                    }
                }
                tbody {
                    val periods = item.timer.periods()
                    periods.keys.sortedBy { it.at }.forEach { acquired ->
                        val period = periods.getValue(acquired)
                        val endPrediction = sequenceOf(period.predictedEnd()?.takeIf { period.earlyEnded == null })
                            .filterNotNull()
                            .map {
                                Either.b<TimerEvent, Instant>(it)
                            }
                        val events = (endPrediction + period.events.asSequence().map { Either.a(it) })
                            .sortedWith { a, b ->
                                val aAt = a.a?.at ?: a.requireB
                                val bAt = b.a?.at ?: b.requireB
                                val diff = aAt.compareTo(bAt)
                                when {
                                    diff != 0 -> diff
                                    a.hasB -> -1
                                    b.hasB -> 1
                                    else -> 0
                                }
                            }
                        events.forEachIndexed { index, either ->
                            if (either.hasB) {
                                val endTime = either.requireB
                                tr {
                                    td {
                                        +(endTime.coerceAtMost(now)).formatted()
                                    }
                                    td {
                                        +if (endTime <= now) {
                                            "Finalizado"
                                        } else {
                                            "Agora"
                                        }
                                    }
                                    td("text-center text-gray") {
                                        small("text-gray") {
                                            +"N/A"
                                        }
                                    }
                                    td {
                                        val remaining = period.timeRemaining(at = endTime.coerceAtMost(now))
                                        classes = when {
                                            remaining <= 0.seconds -> "text-center text-danger"
                                            else -> "text-center text-success"
                                        }
                                        +remaining.toClockFormat(
                                            days = true,
                                            hours = true,
                                            minutes = true,
                                            seconds = true,
                                            millis = false
                                        )
                                    }
                                    td("text-gray") {
                                        small {
                                            +"N/A"
                                        }
                                    }
                                    td {
                                        +if (endTime <= now) {
                                            "O tempo restante atingiu 0 segundos"
                                        } else {
                                            "Tempo que falta para o período ser encerrado"
                                        }
                                    }
                                }
                                return@forEachIndexed
                            }
                            val event = either.requireA
                            tr {
                                td {
                                    if (index == 0) {
                                        style = jso {
                                            borderTopWidth = 2.px
                                            borderTopColor = NamedColor.purple
                                        }
                                    }
                                    +event.at.formatted()
                                }
                                td {
                                    if (index == 0) {
                                        style = jso {
                                            borderTopWidth = 2.px
                                            borderTopColor = NamedColor.purple
                                        }
                                    }
                                    +when (event) {
                                        is TimerEvent.Completed -> "Completado"
                                        is TimerEvent.EarlyEnded -> "Cancelado"
                                        is TimerEvent.Paused -> "Pausado"
                                        is TimerEvent.ResumeAvailable -> "Liberado"
                                        is TimerEvent.Resumed -> "Continuado"
                                        is TimerEvent.Started -> "Iniciado"
                                        is TimerEvent.Acquired -> "Obtido"
                                        is TimerEvent.AcquiredExtra -> "Obtido Extra"
                                        is TimerEvent.Revoked -> "Revogado"
                                    }
                                }
                                td {
                                    if (index == 0) {
                                        style = jso {
                                            borderTopWidth = 2.px
                                            borderTopColor = NamedColor.purple
                                        }
                                    }
                                    val change = when (event) {
                                        is TimerEvent.TimeIncrease -> event.time
                                        is TimerEvent.EarlyEnded -> -period.timeRemaining(at = event.at, ignoreEarlyEnd = true)
                                        is TimerEvent.Completed -> null
                                        is TimerEvent.Paused -> null
                                        is TimerEvent.ResumeAvailable -> null
                                        is TimerEvent.Resumed -> null
                                        is TimerEvent.Started -> null
                                    }
                                    classes = when {
                                        change == 0.seconds || change == null -> "text-center text-gray"
                                        change < 0.seconds -> "text-center text-danger"
                                        else -> "text-center text-success"
                                    }
                                    if (change != null) {
                                        if (change > 0.seconds) +"+"
                                        +change.toClockFormat(
                                            days = true,
                                            hours = true,
                                            minutes = true,
                                            seconds = true,
                                            millis = false
                                        )
                                    } else {
                                        small {
                                            +"N/A"
                                        }
                                    }
                                }
                                td {
                                    if (index == 0) {
                                        style = jso {
                                            borderTopWidth = 2.px
                                            borderTopColor = NamedColor.purple
                                        }
                                    }
                                    val remaining = period.timeRemaining(at = event.at)
                                    classes = when {
                                        remaining <= 0.seconds -> "text-center text-danger"
                                        else -> "text-center text-success"
                                    }
                                    +remaining.toClockFormat(
                                        days = true,
                                        hours = true,
                                        minutes = true,
                                        seconds = true,
                                        millis = false
                                    )
                                }
                                td {
                                    if (index == 0) {
                                        style = jso {
                                            borderTopWidth = 2.px
                                            borderTopColor = NamedColor.purple
                                        }
                                    }
                                    val orderId = when (event) {
                                        is TimerEvent.TimeIncrease -> event.orderId
                                        is TimerEvent.EarlyEnded -> item.amount.events.firstOrNull { it.id == event.decrementId }?.orderId
                                        else -> null
                                    }
                                    Option.of(orderId)
                                        .ifEmpty {
                                            small("text-gray") {
                                                +"N/A"
                                            }
                                        }.ifPresent {
                                            Link {
                                                to = "${LoggedSitemap.PAY_ROOT.path}/$it"
                                                +it.toString()
                                            }
                                        }
                                }
                                td {
                                    if (index == 0) {
                                        style = jso {
                                            borderTopWidth = 2.px
                                            borderTopColor = NamedColor.purple
                                        }
                                    }
                                    when (event) {
                                        is TimerEvent.Completed -> m(-"${"Jogadora"/profile} ${"notificada"/profile} sobre término do período")
                                        is TimerEvent.EarlyEnded -> +updateReason[event.decrementId]
                                        is TimerEvent.Paused -> +"Pausado por ${"uma"/null} ${"administradora"/null}"
                                        is TimerEvent.ResumeAvailable -> +"Pausa encerrada e pronto para ser continuado"
                                        is TimerEvent.Resumed -> m(-"Resumido ${"pela"/profile} ${"jogadora"/profile}")
                                        is TimerEvent.Started -> m(-"Iniciado ${"pela"/profile} ${"jogadora"/profile}")
                                        is TimerEvent.Acquired -> +updateReason[event.incrementId]
                                        is TimerEvent.AcquiredExtra -> +"Obtido tempo extra"
                                        is TimerEvent.Revoked -> +updateReason[event.decrementId]
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
